import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, Loader2, Sparkles, Upload, ArrowRight, ShieldCheck, MapPin, Users, X, Tag, Flame } from "lucide-react";
import { toast } from "sonner";

const ACCENTS = [
  { ring: "ring-amber-300/60", glow: "from-amber-400/25", chip: "bg-amber-400 text-amber-950" },
  { ring: "ring-sky-300/60", glow: "from-sky-400/25", chip: "bg-sky-400 text-sky-950" },
  { ring: "ring-emerald-300/60", glow: "from-emerald-400/25", chip: "bg-emerald-400 text-emerald-950" },
  { ring: "ring-fuchsia-300/60", glow: "from-fuchsia-400/25", chip: "bg-fuchsia-400 text-fuchsia-950" },
];

export default function Packages() {
  const [params, setParams] = useSearchParams();
  const sedeParam = params.get("sede") || "";
  const embed = params.get("embed") === "1";

  const [sedes, setSedes] = useState([]);
  const [packages, setPackages] = useState(null);
  const [picked, setPicked] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [receipt, setReceipt] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  // Cupón
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null); // { ok, discount, final_price, original_price, reason }
  const [checkingCoupon, setCheckingCoupon] = useState(false);

  useEffect(() => {
    api.get("/store/sedes").then((r) => setSedes(r.data || [])).catch(() => setSedes([]));
  }, []);

  useEffect(() => {
    setPackages(null);
    const url = sedeParam ? `/store/packages?sede=${sedeParam}` : "/store/packages";
    api.get(url).then((r) => setPackages(r.data || [])).catch(() => setPackages([]));
  }, [sedeParam]);

  const chooseSede = (id) => {
    const next = new URLSearchParams(params);
    if (id) next.set("sede", id); else next.delete("sede");
    setParams(next, { replace: true });
  };

  const applyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code || !picked) return;
    try {
      setCheckingCoupon(true);
      const r = await api.post("/store/coupons/validate", {
        code, product_id: picked.id, email: form.email || null,
      });
      setCoupon({ ...r.data, code });
      if (r.data.ok) toast.success(`Cupón aplicado · -USD ${Number(r.data.discount).toFixed(2)}`);
      else toast.error(r.data.reason || "Código no válido");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos validar el código");
    } finally { setCheckingCoupon(false); }
  };

  const clearCoupon = () => { setCouponCode(""); setCoupon(null); };

  const effectivePrice = coupon?.ok ? coupon.final_price : (picked?.price ?? 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!receipt) return toast.error("Adjuntá el comprobante de pago");
    const fd = new FormData();
    fd.append("product_id", picked.id);
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (coupon?.ok && couponCode) fd.append("coupon_code", couponCode.trim().toUpperCase());
    fd.append("receipt", receipt);
    try {
      setSending(true);
      await api.post("/store/orders", fd);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos enviar tu pedido. Intentá de nuevo.");
    } finally { setSending(false); }
  };

  const wrapperClass = embed
    ? "min-h-[unset] bg-transparent text-slate-900"
    : "min-h-screen bg-[#0b1220] text-white selection:bg-amber-300 selection:text-amber-950";

  return (
    <div className={wrapperClass}>
      {!embed && (
        <div className="pointer-events-none fixed inset-0 opacity-70"
          style={{ background: "radial-gradient(60% 50% at 15% 0%, #1d4ed8 0%, transparent 60%), radial-gradient(50% 40% at 90% 10%, #7c3aed 0%, transparent 55%)" }} />
      )}

      {!embed && (
        <header className="relative px-6 pt-16 pb-8 sm:pt-24 max-w-6xl mx-auto">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold tracking-wide backdrop-blur">
            <Sparkles className="h-3.5 w-3.5 text-amber-300" /> We Speak Spanish · Costa Rica
          </span>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] max-w-3xl">
            Elegí tu paquete y empezá<br /><span className="text-amber-300">a hablar español</span> esta semana.
          </h1>
          <p className="mt-5 max-w-xl text-base text-white/70">
            Clases con profesoras ticas certificadas, en línea o presenciales. Reservás tu paquete,
            nosotros coordinamos tu horario.
          </p>
        </header>
      )}

      <main className={`relative ${embed ? "px-0 py-6" : "px-6 pb-24"} max-w-6xl mx-auto`} data-testid="store-packages">
        {sedes.length > 1 && (
          <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="store-sede-filter">
            <button
              onClick={() => chooseSede("")}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold transition ${
                !sedeParam
                  ? (embed ? "bg-slate-900 text-white" : "bg-white text-slate-900")
                  : (embed ? "border border-slate-300 text-slate-700 hover:bg-slate-100" : "border border-white/20 text-white/80 hover:bg-white/10")
              }`}
              data-testid="sede-all"
            >
              <MapPin className="h-3.5 w-3.5" /> Todas las sedes
            </button>
            {sedes.map((s) => (
              <button
                key={s.id}
                onClick={() => chooseSede(s.id)}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold transition ${
                  sedeParam === s.id
                    ? (embed ? "bg-slate-900 text-white" : "bg-white text-slate-900")
                    : (embed ? "border border-slate-300 text-slate-700 hover:bg-slate-100" : "border border-white/20 text-white/80 hover:bg-white/10")
                }`}
                data-testid={`sede-${s.id}`}
              >
                {s.name}
              </button>
            ))}
          </div>
        )}

        {packages === null ? (
          <div className={`flex items-center gap-2 ${embed ? "text-slate-500" : "text-white/60"}`}>
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando paquetes…
          </div>
        ) : packages.length === 0 ? (
          <div className={`rounded-3xl p-10 text-center ${embed ? "border border-slate-200 bg-slate-50 text-slate-500" : "border border-white/10 bg-white/5 text-white/70"}`}
            data-testid="store-empty">
            Todavía no hay paquetes publicados{sedeParam ? " para esta sede" : ""}. Escribinos y te armamos uno.
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {packages.map((p, i) => {
              const a = ACCENTS[i % ACCENTS.length];
              const cardBase = embed
                ? "bg-white border border-slate-200 shadow-sm hover:shadow-md"
                : "border border-white/10 bg-white/[0.04] hover:bg-white/[0.07] ring-1 ring-transparent backdrop-blur";
              return (
                <article key={p.id}
                  className={`group relative overflow-hidden rounded-3xl p-6 transition-all duration-300 hover:-translate-y-1 ${cardBase} hover:${a.ring}`}
                  data-testid={`package-${p.id}`}>
                  <div className={`pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-gradient-to-br ${a.glow} to-transparent blur-2xl`} />
                  <div className="flex flex-wrap items-center gap-1.5">
                    {p.modality && (
                      <span className={`inline-block rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${a.chip}`}>{p.modality}</span>
                    )}
                    {p.type === "private" && (
                      <span className={`inline-block rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${embed ? "bg-slate-900 text-white" : "bg-white text-slate-900"}`}>Privado</span>
                    )}
                    {p.highlight && (
                      <span className="inline-block rounded-full bg-amber-400 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-950">Favorito</span>
                    )}
                  </div>
                  <h2 className="mt-3 font-display text-2xl font-bold">{p.name}</h2>
                  {p.sede_name && (
                    <div className={`mt-1 flex items-center gap-1 text-xs ${embed ? "text-slate-500" : "text-white/60"}`}>
                      <MapPin className="h-3 w-3" /> {p.sede_name}
                    </div>
                  )}
                  <div className="mt-4 flex items-end gap-2 flex-wrap">
                    <span className="font-display text-4xl font-bold">${Number(p.price).toFixed(0)}</span>
                    {p.original_price && (
                      <span className={`pb-1 text-sm line-through ${embed ? "text-slate-400" : "text-white/40"}`} data-testid={`orig-price-${p.id}`}>
                        ${Number(p.original_price).toFixed(0)}
                      </span>
                    )}
                    <span className={`pb-1 text-xs ${embed ? "text-slate-400" : "text-white/50"}`}>USD {p.classes_included ? `/ ${p.classes_included} clases` : ""}</span>
                  </div>
                  {p.launch_active && (
                    <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-rose-500/15 border border-rose-500/40 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-rose-500" data-testid={`launch-${p.id}`}>
                      <Flame className="h-3 w-3" /> Precio de lanzamiento
                    </div>
                  )}
                  {p.description && <p className={`mt-4 text-sm leading-relaxed ${embed ? "text-slate-600" : "text-white/70"}`}>{p.description}</p>}
                  <ul className={`mt-4 space-y-1.5 text-sm ${embed ? "text-slate-700" : "text-white/75"}`}>
                    {p.classes_included > 0 && <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-500 shrink-0" />{p.classes_included} clases en tu ciclo</li>}
                    <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-500 shrink-0" />Profesora asignada y horario a tu medida</li>
                    <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-500 shrink-0" />Plataforma con tareas, recursos y WSS Live</li>
                  </ul>

                  {p.type === "group" && p.group_capacity ? (
                    <div className={`mt-4 flex items-center gap-2 rounded-xl px-3 py-2 text-xs ${
                      p.is_full
                        ? "bg-rose-500/15 text-rose-700 border border-rose-500/30"
                        : (p.seats_left <= 2
                            ? "bg-amber-500/15 text-amber-700 border border-amber-500/30"
                            : (embed ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30"))
                    }`} data-testid={`seats-${p.id}`}>
                      <Users className="h-3.5 w-3.5" />
                      {p.is_full ? "Grupo lleno · escribinos para siguiente ciclo"
                        : (p.seats_left === 1 ? "¡Último cupo!" : `${p.seats_left} de ${p.group_capacity} cupos libres`)}
                    </div>
                  ) : null}

                  <button
                    onClick={() => { if (!p.is_full) { setPicked(p); setDone(false); setForm({ name: "", email: "", phone: "", notes: "" }); setReceipt(null); clearCoupon(); } }}
                    disabled={p.is_full}
                    className={`mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold transition-transform ${
                      p.is_full
                        ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                        : (embed ? "bg-slate-900 text-white hover:scale-[1.02] active:scale-95" : "bg-white text-slate-900 hover:scale-[1.02] active:scale-95")
                    }`}
                    data-testid={`pick-package-${p.id}`}>
                    {p.is_full ? "Sin cupo" : (<>Lo quiero <ArrowRight className="h-4 w-4" /></>)}
                  </button>
                </article>
              );
            })}
          </div>
        )}

        {picked && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
            onClick={(e) => { if (e.target === e.currentTarget) setPicked(null); }}>
            <div className="w-full max-w-lg rounded-3xl border border-white/10 bg-[#0f1729] p-6 text-white shadow-2xl" data-testid="store-order-form">
              {done ? (
                <div className="text-center py-6" data-testid="store-order-done">
                  <div className="mx-auto h-16 w-16 rounded-full bg-emerald-400/15 border-2 border-emerald-400 flex items-center justify-center">
                    <Check className="h-8 w-8 text-emerald-300" />
                  </div>
                  <h3 className="mt-5 font-display text-2xl font-bold">¡Pedido recibido!</h3>
                  <p className="mt-2 text-sm text-white/70">
                    Verificamos tu pago y te escribimos a <strong>{form.email}</strong> con tu acceso
                    y las opciones de horario.
                  </p>
                  <button onClick={() => setPicked(null)} className="mt-6 rounded-full bg-white px-6 py-2.5 text-sm font-bold text-slate-900"
                    data-testid="store-order-close">Cerrar</button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-300">Tu paquete</p>
                      <h3 className="font-display text-xl font-bold">
                        {picked.name} · ${Number(effectivePrice).toFixed(0)} USD
                        {coupon?.ok && (
                          <span className="ml-2 text-sm font-normal text-white/50 line-through">${Number(picked.price).toFixed(0)}</span>
                        )}
                      </h3>
                      {picked.sede_name && <p className="text-xs text-white/60 mt-0.5"><MapPin className="h-3 w-3 inline mr-1" />{picked.sede_name}</p>}
                    </div>
                    <button type="button" onClick={() => setPicked(null)} className="rounded-full p-1 text-white/60 hover:bg-white/10" data-testid="store-close-x"><X className="h-4 w-4" /></button>
                  </div>
                  {picked.payment_link && (
                    <a href={picked.payment_link} target="_blank" rel="noreferrer"
                      className="flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-amber-400 to-amber-300 px-5 py-3 text-sm font-bold text-amber-950 hover:brightness-105"
                      data-testid="store-pay-link">
                      <ShieldCheck className="h-4 w-4" /> 1· Pagar ${Number(effectivePrice).toFixed(0)} con tarjeta
                    </a>
                  )}
                  <p className="text-xs text-white/60">
                    2· Completá tus datos y adjuntá el comprobante. También podés pagar por SINPE o transferencia
                    y subir el comprobante acá.
                  </p>
                  {[["name", "Nombre completo", "text"], ["email", "Correo", "email"], ["phone", "Teléfono / WhatsApp", "tel"]].map(([k, label, type]) => (
                    <input key={k} type={type} required={k !== "phone"} placeholder={label} value={form[k]}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-sm placeholder:text-white/40 focus:border-amber-300 focus:outline-none"
                      data-testid={`store-field-${k}`} />
                  ))}
                  <textarea placeholder="¿Qué días y horas te quedan mejor? (opcional)" value={form.notes} rows={2}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-sm placeholder:text-white/40 focus:border-amber-300 focus:outline-none"
                    data-testid="store-field-notes" />

                  {/* Cupón */}
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                      <input
                        type="text"
                        placeholder="¿Tenés un código?"
                        value={couponCode}
                        onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); if (coupon) setCoupon(null); }}
                        className={`w-full rounded-xl border pl-9 pr-4 py-2.5 text-sm bg-white/5 focus:outline-none ${
                          coupon?.ok ? "border-emerald-400/60" : (coupon && !coupon.ok ? "border-rose-400/60" : "border-white/15 focus:border-amber-300")
                        }`}
                        data-testid="coupon-input"
                      />
                    </div>
                    {coupon?.ok ? (
                      <button type="button" onClick={clearCoupon}
                        className="rounded-xl border border-white/20 px-3 text-xs font-semibold text-white/80 hover:bg-white/10"
                        data-testid="coupon-clear">Quitar</button>
                    ) : (
                      <button type="button" onClick={applyCoupon} disabled={!couponCode || checkingCoupon}
                        className="rounded-xl bg-white/10 px-4 text-xs font-bold text-white hover:bg-white/20 disabled:opacity-40"
                        data-testid="coupon-apply">
                        {checkingCoupon ? <Loader2 className="h-3 w-3 animate-spin" /> : "Aplicar"}
                      </button>
                    )}
                  </div>
                  {coupon?.ok && (
                    <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-300 flex items-center justify-between" data-testid="coupon-applied">
                      <span><strong>{couponCode}</strong> · -USD {Number(coupon.discount).toFixed(2)}</span>
                      <span className="font-bold">Total: USD {Number(coupon.final_price).toFixed(2)}</span>
                    </div>
                  )}
                  {coupon && !coupon.ok && (
                    <p className="text-xs text-rose-300" data-testid="coupon-error">{coupon.reason}</p>
                  )}

                  <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-white/25 bg-white/5 px-4 py-3 text-sm hover:border-amber-300">
                    <Upload className="h-4 w-4 text-amber-300" />
                    <span className="truncate">{receipt ? receipt.name : "Adjuntar comprobante (imagen o PDF)"}</span>
                    <input type="file" accept="image/*,.pdf" className="hidden" data-testid="store-field-receipt"
                      onChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        if (f && f.size > 10 * 1024 * 1024) { toast.error("El comprobante no puede pasar de 10 MB"); e.target.value = ""; return; }
                        setReceipt(f);
                      }} />
                  </label>
                  <div className="flex gap-2 pt-1">
                    <button type="button" onClick={() => setPicked(null)}
                      className="flex-1 rounded-full border border-white/20 px-5 py-3 text-sm font-semibold text-white/80"
                      data-testid="store-cancel">Cancelar</button>
                    <button type="submit" disabled={sending}
                      className="flex-[2] inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-bold text-slate-900 disabled:opacity-60"
                      data-testid="store-submit">
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Enviar mi pedido
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
